/* eslint-disable @typescript-eslint/no-empty-function */
import { JSONSchema7, JSONSchema7Definition } from 'json-schema';
import * as React from 'react';
import { Key, mutate } from 'swr';
import { z } from 'zod';

import { RunSchema } from '@/types/schema';
import { useLocalStorageState } from '@/utils/use-local-storage-state';

import { ThreadState, useLangGraphClient } from '../api';
import {
  useGraphThreadHistory,
  useGraphThreadHistoryMutate,
} from '../api/useGraphSwr';
import { useRegisterTraceLogToDatasetStore } from '../components/debug/dataset/GraphDatasetTraceLogStore';
import { TraceLog, Updates, isCheckpoint } from '../data/misc';
import * as Tree from '../data/tree';
import { useActiveThreadId } from '../hooks/useActiveThreadId';
import { useAssistantStore } from '../store/assistants/assistantsStore';
import { RunState, isRunExecuting } from './state.utils';
import { useTraceLog } from './trace';

export type UntypedInput = Record<string, unknown>;

export type SchemaBundle = {
  state: JSONSchema7Definition | undefined | null;
  stateRoot: JSONSchema7 | undefined | null;
  config: JSONSchema7Definition | undefined | null;
  configRoot: JSONSchema7 | undefined | null;
  input: JSONSchema7Definition | undefined | null;
  inputRoot: JSONSchema7 | undefined | null;
  output: JSONSchema7Definition | undefined | null;
  outputRoot: JSONSchema7 | undefined | null;
};

export type StateContext = {
  // ---- Breakpoints ----
  beforeBreakpoints: string[];
  addBeforeBreakpoint: (bp: string) => void;
  setBeforeBreakpoints: (bps: string[]) => void;
  removeBeforeBreakpoint: (bp: string) => void;
  clearBeforeBreakpoints: () => void;

  afterBreakpoints: string[];
  addAfterBreakpoint: (bp: string) => void;
  setAfterBreakpoints: (bps: string[]) => void;
  removeAfterBreakpoint: (bp: string) => void;
  clearAfterBreakpoints: () => void;

  // ---- Updates ----
  updates: Array<Updates>;
  addUpdate: (update: Updates) => void;
  clearUpdates: () => void;
  pendingMessages: Record<string, unknown[]>;
  setPendingMessages: (msgs: Record<string, unknown[]>) => void;
  clearPendingMessages: () => void;

  // ---- Selection ----
  hoverSelection: string | null;
  setHoverSelection: React.Dispatch<React.SetStateAction<string | null>>;

  editingCheckpointId: string | null;
  setEditingCheckpointId: (id: string | null) => void;

  selectedPlaygroundRun: RunSchema | undefined;
  setSelectedPlaygroundRun: (run: RunSchema | undefined) => void;

  subgraphSelection: string[];
  toggleSubgraphSelection: (namespace: string) => void;
  clearSubgraphSelection: () => void;

  collapsedIds: string[];
  toggleCollapsedId: (id: string) => void;

  // ---- Trace Log ----
  traceLog: TraceLog[];
  runIdsMap: Record<string, string>;
  forestPath: Tree.ForestPath;
  setForestPath: (path: Tree.ForestPath) => void;
  threadHead: ThreadState | undefined;
  traceLoading: boolean;
  traceError: unknown | undefined;
  traceValidating: boolean;

  // ---- Run State ----
  runState: RunState;
  setRunState: (runState: RunState) => void;
  threadId: string | undefined;
  // ---- View Raw ----
  viewRaw: boolean;
  toggleViewRaw: () => void;
};

export type RenderModeContext = {
  // ---- Render Mode ----
  renderMode: 'data' | 'pretty';
  setRenderMode: (mode: 'data' | 'pretty') => void;
  renderPrettyOptions: { allow: Array<'markdown'> };
  setRenderPrettyOptions: (options: { allow: Array<'markdown'> }) => void;
};

export const StateContext = React.createContext<StateContext>({
  // ---- Breakpoints ----
  beforeBreakpoints: [],
  addBeforeBreakpoint: () => {},
  setBeforeBreakpoints: () => {},
  removeBeforeBreakpoint: () => {},
  clearBeforeBreakpoints: () => {},

  afterBreakpoints: [],
  addAfterBreakpoint: () => {},
  setAfterBreakpoints: () => {},
  removeAfterBreakpoint: () => {},
  clearAfterBreakpoints: () => {},

  // ---- Updates ----
  updates: [],
  addUpdate: () => {},
  clearUpdates: () => {},
  pendingMessages: {},
  setPendingMessages: () => {},
  clearPendingMessages: () => {},

  // ---- Selection ----
  hoverSelection: null,
  setHoverSelection: () => {},
  editingCheckpointId: null,
  setEditingCheckpointId: () => {},
  subgraphSelection: [],
  toggleSubgraphSelection: () => {},
  clearSubgraphSelection: () => {},
  collapsedIds: [],
  toggleCollapsedId: () => {},
  selectedPlaygroundRun: undefined,
  setSelectedPlaygroundRun: () => {},

  // ---- Trace Log ----
  traceLog: [],
  runIdsMap: {},
  forestPath: [],
  setForestPath: () => {},
  threadHead: undefined,
  threadId: undefined,
  traceLoading: false,
  traceError: undefined,
  traceValidating: false,

  // ---- Run State ----
  runState: { type: 'empty' },
  setRunState: () => {},

  // ---- View Raw ----
  viewRaw: false,
  toggleViewRaw: () => {},
});

export const RenderModeContext = React.createContext<RenderModeContext>({
  // ---- Render Mode ----
  renderMode: 'data',
  setRenderMode: () => {},
  renderPrettyOptions: { allow: [] },
  setRenderPrettyOptions: () => {},
});

export const deepClone = <T>(obj: T): T => {
  if ('structuredClone' in self) {
    return structuredClone(obj);
  }

  return JSON.parse(JSON.stringify(obj));
};

const BreakpointsSchema = z.object({
  beforeBreakpoints: z.array(z.string()),
  afterBreakpoints: z.array(z.string()),
});

export function getStoredBreakpoints(assistantId: string): {
  beforeBreakpoints: string[];
  afterBreakpoints: string[];
} {
  const item = window.localStorage.getItem(
    `ls:studio:breakpoints:${assistantId}`
  );

  if (item) {
    const parsed = BreakpointsSchema.safeParse(JSON.parse(item));
    if (parsed.success) return parsed.data;
  }
  return { beforeBreakpoints: [], afterBreakpoints: [] };
}

const useSyncStoreBreakpoints = (state: {
  beforeBreakpoints: string[];
  afterBreakpoints: string[];
}) => {
  const { activeAssistantId } = useAssistantStore();
  const { beforeBreakpoints, afterBreakpoints } = state;
  React.useEffect(() => {
    const key = `ls:studio:breakpoints:${activeAssistantId}`;
    if (beforeBreakpoints.length === 0 && afterBreakpoints.length === 0) {
      window.localStorage.removeItem(key);
      return;
    }

    window.localStorage.setItem(
      key,
      JSON.stringify({ beforeBreakpoints, afterBreakpoints })
    );
  }, [activeAssistantId, beforeBreakpoints, afterBreakpoints]);
};

export const useStateCtx = ({
  initUpdates,
  initRunState,
  initBreakpoints,
}: {
  initUpdates?: Array<Updates>;
  initRunState: RunState;
  initBreakpoints?: {
    beforeBreakpoints: string[];
    afterBreakpoints: string[];
  };
}): StateContext => {
  const [state, setState] = React.useState<{
    beforeBreakpoints: string[];
    afterBreakpoints: string[];
    updates: Array<Updates>;
    expanded: Selection[];
    collapsedIds: string[];
    searchSelection: Selection[];
    hoverSelection: string | null;
    subgraphSelection: string[];
    input: UntypedInput;
    runState: RunState;
    pendingMessages: Record<string, unknown[]>;
    forestPath: Tree.ForestPath;
    editingCheckpointId: string | null;
    selectedPlaygroundRun: RunSchema | undefined;
  }>(() => ({
    ...(initBreakpoints ?? { beforeBreakpoints: [], afterBreakpoints: [] }),
    updates: initUpdates ?? [],
    expanded: [],
    collapsedIds: [],
    searchSelection: [],
    hoverSelection: null,
    subgraphSelection: [],
    input: {},
    runState: initRunState ?? ({ type: 'empty' } satisfies RunState),
    pendingMessages: {},
    forestPath: [],
    editingCheckpointId: null,
    selectedPlaygroundRun: undefined,
  }));

  const client = useLangGraphClient();

  useSyncStoreBreakpoints({
    beforeBreakpoints: state.beforeBreakpoints,
    afterBreakpoints: state.afterBreakpoints,
  });
  const { threadId } = useActiveThreadId();
  const history = useGraphThreadHistory(
    { threadId },
    {
      revalidateOnFocus: !isRunExecuting(state.runState),
      revalidateOnReconnect: !isRunExecuting(state.runState),
    }
  );
  const historySWRMutate = useGraphThreadHistoryMutate(threadId);

  const { traceLog, threadHead, runIdsMap } = useTraceLog(history.data, {
    forestPath: state.forestPath,
    updates: state.updates,
  });
  useRegisterTraceLogToDatasetStore('$', traceLog);

  // make sure we refresh the history endpoint when the run state changes
  const currExecuting = isRunExecuting(state.runState);
  const prevExecuting = React.useRef(currExecuting);

  React.useEffect(() => {
    if (currExecuting !== prevExecuting.current) {
      if (!currExecuting) {
        // clear task/task result updates and clear pending messages
        setState((s) => ({
          ...s,
          updates: s.updates.filter(isCheckpoint),
          pendingMessages: {},
        }));
      }

      mutate(
        (key: Key) =>
          Array.isArray(key) &&
          key.length === 4 &&
          key[0] === client &&
          key[1] === 'runs' &&
          key[2] === threadId &&
          (key[3] === 'pending' || key[3] === 'running')
      );
      historySWRMutate();
    }
    prevExecuting.current = currExecuting;
  }, [currExecuting, historySWRMutate]);

  const [viewRaw, setViewRaw] = useLocalStorageState(
    'ls:studio:viewRaw',
    false,
    z.boolean()
  );

  const toggleViewRaw = React.useCallback(() => {
    setViewRaw((prev) => !prev);
  }, [setViewRaw]);

  return {
    // ---- Breakpoints ----
    beforeBreakpoints: state.beforeBreakpoints,
    addBeforeBreakpoint: React.useCallback(
      (bp) =>
        setState((s) => ({
          ...s,
          beforeBreakpoints: [
            ...s.beforeBreakpoints.filter((i) => i !== bp),
            bp,
          ],
        })),
      [setState]
    ),
    setBeforeBreakpoints: React.useCallback(
      (bps) => setState((s) => ({ ...s, beforeBreakpoints: bps })),
      [setState]
    ),
    clearBeforeBreakpoints: React.useCallback(
      () => setState((s) => ({ ...s, beforeBreakpoints: [] })),
      [setState]
    ),
    removeBeforeBreakpoint: React.useCallback(
      (bp) =>
        setState((s) => ({
          ...s,
          beforeBreakpoints: s.beforeBreakpoints.filter((b) => b !== bp),
        })),
      [setState]
    ),

    afterBreakpoints: state.afterBreakpoints,
    addAfterBreakpoint: React.useCallback(
      (bp) =>
        setState((s) => ({
          ...s,
          afterBreakpoints: [...s.afterBreakpoints.filter((i) => i !== bp), bp],
        })),
      [setState]
    ),
    setAfterBreakpoints: React.useCallback(
      (bps) => setState((s) => ({ ...s, afterBreakpoints: bps })),
      [setState]
    ),
    clearAfterBreakpoints: React.useCallback(
      () => setState((s) => ({ ...s, afterBreakpoints: [] })),
      [setState]
    ),
    removeAfterBreakpoint: React.useCallback(
      (bp) =>
        setState((s) => ({
          ...s,
          afterBreakpoints: s.afterBreakpoints.filter((b) => b !== bp),
        })),
      [setState]
    ),

    // ---- Updates ----
    updates: state.updates,
    addUpdate: React.useCallback(
      (update) => setState((s) => ({ ...s, updates: [...s.updates, update] })),
      [setState]
    ),
    clearUpdates: React.useCallback(
      () => setState((s) => ({ ...s, updates: [] })),
      [setState]
    ),
    pendingMessages: state.pendingMessages,
    setPendingMessages: React.useCallback(
      (msgs) => setState((s) => ({ ...s, pendingMessages: msgs })),
      [setState]
    ),
    clearPendingMessages: React.useCallback(
      () => setState((s) => ({ ...s, pendingMessages: {} })),
      [setState]
    ),

    // ---- Selection ----
    hoverSelection: state.hoverSelection,
    setHoverSelection: React.useCallback(
      (selection) =>
        setState((s) => {
          if (typeof selection === 'function') {
            return { ...s, hoverSelection: selection(s.hoverSelection) };
          } else {
            return { ...s, hoverSelection: selection };
          }
        }),
      [setState]
    ),
    editingCheckpointId: state.editingCheckpointId,
    setEditingCheckpointId: React.useCallback(
      (id) => setState((s) => ({ ...s, editingCheckpointId: id })),
      [setState]
    ),
    subgraphSelection: state.subgraphSelection,
    toggleSubgraphSelection: React.useCallback(
      (namespace) =>
        setState((s) => ({
          ...s,
          subgraphSelection: s.subgraphSelection.includes(namespace)
            ? s.subgraphSelection.filter((i) => i !== namespace)
            : [...s.subgraphSelection, namespace],
        })),
      [setState]
    ),
    clearSubgraphSelection: React.useCallback(
      () => setState((s) => ({ ...s, subgraphSelection: [] })),
      [setState]
    ),
    collapsedIds: state.collapsedIds,
    toggleCollapsedId: React.useCallback(
      (id) =>
        setState((s) => ({
          ...s,
          collapsedIds: s.collapsedIds.includes(id)
            ? s.collapsedIds.filter((i) => i !== id)
            : [...s.collapsedIds, id],
        })),
      [setState]
    ),

    selectedPlaygroundRun: state.selectedPlaygroundRun,
    setSelectedPlaygroundRun: React.useCallback(
      (run) => setState((s) => ({ ...s, selectedPlaygroundRun: run })),
      [setState]
    ),

    // ---- Trace Log ----
    traceLog,
    runIdsMap,
    forestPath: state.forestPath,
    setForestPath: React.useCallback(
      (path) => setState((s) => ({ ...s, forestPath: path })),
      [setState]
    ),
    threadHead,
    threadId,
    traceLoading: history.isLoading,
    traceError: history.error,
    traceValidating: history.isValidating,

    // ---- Run State ----
    runState: state.runState,
    setRunState: React.useCallback(
      (runState) => setState((s) => ({ ...s, runState })),
      [setState]
    ),

    // ---- View Raw ----
    viewRaw,
    toggleViewRaw,
  };
};

export const useRenderModeCtx = ({
  renderMode,
  enableMarkdown,
}: {
  renderMode?: 'data' | 'pretty';
  enableMarkdown?: boolean;
}): RenderModeContext => {
  const [state, setState] = React.useState<{
    renderMode: 'data' | 'pretty';
    renderPrettyOptions: { allow: Array<'markdown'> };
  }>(() => ({
    renderMode: renderMode ?? 'pretty',
    renderPrettyOptions: { allow: enableMarkdown ? ['markdown'] : [] },
  }));

  return {
    renderMode: state.renderMode,
    setRenderMode: React.useCallback(
      (mode) => setState((s) => ({ ...s, renderMode: mode })),
      [setState]
    ),
    renderPrettyOptions: state.renderPrettyOptions,
    setRenderPrettyOptions: React.useCallback(
      (options) => setState((s) => ({ ...s, renderPrettyOptions: options })),
      [setState]
    ),
  };
};
